const title = "How to format your data";
const description = "Learn about data format requirements.";

const Tutorial = ({title}) => (
    <>
    <h3>{title}</h3>
    <p>
      In this tutorial we will cover how to prepare a lipidomics dataset for use with Fatty Acylizer.
    </p>
    <h4>Load tutorial dataset</h4>
    <p>
      For this demonstration we will use a dataset from a <cite><a href="https://doi.org/10.1016/j.jlr.2021.100111">publication</a></cite> in which we investigated the impact of external fatty acid supplementation on the cardiolipin profiles of HeLa cells.
      The <cite><a href="https://doi.org/10.17632/6pdcrm5p6k.1">data repository</a></cite> contains both raw data files and the quantified results.
      For Fatty Acylizer, we only need quantified results. Download the dataset to proceed.
    </p>
    <p>
      Open the dataset in a spreadsheet program of your choice.
      The first column contains the assay names of the dataset and the first row contains the lipid names.
      The last column contains grouping information.
    </p>
    <h4>General data layout</h4>
    <p>
      Fatty Acylizer interprets the first row as assay names and the first column as lipids.
      To get your data into the correct format, you need to transpose the table.
      In <i>LibreOffice Calc</i> we can do this by first selecting the dataset,
      cutting the data with <kbd>CTRL</kbd>+<kbd>X</kbd>,
      and pasting it by right clicking and selecting <q>Paste special</q> and <q>Transpose</q>.
      It works analogously in <i>Microsoft Excel</i>.
    </p>
    <h4>Lipid nomenclature</h4>
    <p>
      Next, we need to check the validity of the used lipid nomenclature.
      Fatty Acylizer uses <cite><a href="https://doi.org/10.1021/acs.analchem.0c01690">Goslin</a></cite> to parse the lipid names.
      Therefore we can quickly check our nomenclature using the <a href="https://apps.lifs-tools.org/goslin/validate">Goslin web application</a>.
      Remove the examples and test the used lipid nomenclature of your dataset.
      For the HeLa dataset, we need to remove the colon between the lipid class specifier and the number of carbon atoms to obtain a valid lipid.
      This can be done quickly using <q>Find and Replace</q> in your spreadsheet program.
    </p>
    <h4>Missing values</h4>
    <p>
      The values of the quantification table must be numbers.
      Often missing values are reported by a <code>NaN</code>, <code>Null</code>, <code>None</code>, or other variations.
      Depending on the data format different representations are recognized as missing value.
      Therefore, it is usually best to simply remove these values entirely, and leave these cells empty instead.
    </p>
    <h4>Internal Standards</h4>
    <p>
      To quantify lipids typically an internal standard is used.
      The fatty acids of the internal standard should not be part of our reconstructed fatty acyl profiles.
      For this dataset, <i>CL 56:0</i> was used as the internal standard and should be removed from the dataset.
      The quantitative data is automatically normalized by Fatty Acylizer before the reconstruction.
    </p>
    <h4>Extra metadata and study variables</h4>
    <p>
      Lastly, we need to remove the "group" row at the bottom of the table.
      Fatty Acylizer expects only quantified lipid data in this spreadsheet format.
      If you use an analysis software than can export to <cite><a href="https://pubs.acs.org/doi/10.1021/acs.analchem.8b04310">mzTab-M format</a></cite>, 
      you can communicate this group information to Fatty Acylizer via <dfn>study variables</dfn> that can store the group information.
      For each study variable, the summary table contains an aggregated mean, and standard deviation value for each group.
      If you use such a mzTab-M file for Fatty Acylizer,
      the final report.mzTab file in the downloaded ZIP-archive, contains the same study variables,
      and reports the mean and standard deviation within each group.
      If your analysis software does not support exporting to mzTab-M yet,
      you will have to resolve to grouping the data using regular statistical software after analysis with Fatty Acylizer.
    </p>
    <h4>References</h4>
    <dl>
      <dt>
        Fatty acyl availability modulates cardiolipin composition and alters mitochondrial function in HeLa cells
      </dt>
      <dd>
        <a href="https://doi.org/10.1016/j.jlr.2021.100111">
          <small>Journal of Lipid Research, Volume 62, 2021, Pages 100111</small>
        </a>
        <br/>
        Gregor Oemer, Marie-Luise Edenhofer, Yvonne Wohlfarter, Katharina Lackner, Geraldine Leman, Jakob Koch, Luiza H.D. Cardoso, Herbert H. Lindner, Erich Gnaiger, Sandrine Dubrac, Johannes Zschocke, Markus A. Keller
        <br />
        <a href="https://doi.org/10.17632/6pdcrm5p6k.1">
          Data repository
        </a>
      </dd>
      <dt>Goslin: A Grammar of Succinct Lipid Nomenclature</dt>
      <dd>
        <a href="https://doi.org/10.1021/acs.analchem.0c01690">
          <small>Analytical Chemistry, Volume 92, 2020, Pages 10957-10960</small>
        </a>
        <br />
        Dominik Kopczynski, Nils Hoffmann, Bing Peng, Robert Ahrends
        <br />
        <a href="https://apps.lifs-tools.org/goslin/validate">Goslin web application</a>
      </dd>
      <dt>mzTab-M: A Data Standard for Sharing Quantitative Results in Mass Spectrometry Metabolomics</dt>
      <dd>
        <a href="https://pubs.acs.org/doi/10.1021/acs.analchem.8b04310">
          <small>Analytical Chemistry, Volume 91, 2019, Pages 3302-3310</small>
        </a>
        <br />
        Nils Hoffmann, Joel Rein, Timo Sachsenberg, Jürgen Hartler, Kenneth Haug, Gerhard Mayer, Oliver Alka, Saravanan Dayalan, Jake T. M. Pearce, Philippe Rocca-Serra, Da Qi, Martin Eisenacher, Yasset Perez-Riverol, Juan Antonio Vizcaíno, Reza M. Salek, Steffen Neumann, Andrew R. Jones
      </dd>
    </dl>
  </>
)

export const PreprocessingTutorial = ({prefix}) => {
  const title_ = prefix ? prefix + title : title;
  return {
    title: title_,
    section: "preprocessing",
    description: description,
    tutorial: () => Tutorial({title: title_})
  }
}
